import { Card } from '@components/atoms/Card';
import { Badge } from '@components/atoms/Badge';
import Alert from '@components/atoms/Alert';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import { formatActivityTime, formatForDisplay } from '@utils/formatDate';
import useFormattedCurrency from '@hooks/useFormattedCurrency';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import { ActivityHeader } from '../ActivityHeader';
import { TaskName } from '../TaskName';
import { useTranslation } from '@hooks/useTranslation';

const formatDate = (date?: string | Date | null, notAvailable = 'N/A') =>
  date ? formatForDisplay(date) : notAvailable;

export const ActivityBudgetRequestSubmitted = ({
  activity,
  handleOpenTaskDetail,
}: {
  activity: any;
  handleOpenTaskDetail?: (taskId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const maxBudget = activity.budgetRequest?.maxBudgetAmount || null;
  const proposedAmount = activity.budgetProposal?.proposedAmount || null;
  const formattedMaxBudget = useFormattedCurrency(maxBudget);
  const formattedProposed = useFormattedCurrency(proposedAmount);

  const requestedStart = activity.budgetRequest?.requestedStartDate;
  const requestedEnd = activity.budgetRequest?.requestedEndDate;
  const proposedStart = activity.budgetProposal?.proposedStartDate;
  const proposedEnd = activity.budgetProposal?.proposedEndDate;

  const recipients = activity.budgetRequest?.recipients || [];

  const rightLabel = (
    <div className="flex gap-1">
      <TextSnippetOutlinedIcon fontSize="small" className="text-yellow-800" />
      <div className="flex gap-1">
        <p className="text-sm text-yellow-800">
          {tActivityCards.budgetRequestSubmitted.bidProposalSubmitted}
        </p>
        <p className="text-sm text-neutral-500">
          {activity.createdAt &&
            formatActivityTime(activity.createdAt)}
        </p>
      </div>
    </div>
  );

  const originatorOrg = activity.budgetRequest?.originatorOrganization as
    | { organizationId?: string; name?: string }
    | undefined;
  const proposingOrg = activity.budgetProposal?.proposingOrganization as
    | { organizationId?: string; name?: string }
    | undefined;

  return (
    <Card key={activity.activityId} className="w-full">
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="flex gap-1 mt-2">
        <TaskName
          task={activity.task}
          taskId={activity.taskId || activity.task?.taskId}
          onOpenTask={handleOpenTaskDetail}
        />
      </div>

      <div className="flex gap-2 w-full">
        <div className="flex flex-col flex-1 gap-2">
          <Alert className="mt-2">
            <div className="flex flex-col gap-1">
              <div className="flex gap-1">
                <p className="text-sm text-neutral-800 font-semibold">
                  {tActivityCards.taskTypeLabel}
                </p>
                {activity.task?.taskType && (
                  <p className="text-sm text-neutral-800">
                    {activity.task.taskType.split('_').join(' ')}
                  </p>
                )}
              </div>
              <div className="flex gap-1">
                <p className="text-sm text-neutral-800 font-semibold">
                  {tActivityCards.projectLabel}
                </p>
                <p className="text-sm text-neutral-800">
                  {activity.project?.name || tActivityCards.notAvailable}
                </p>
              </div>
            </div>
          </Alert>

          <Alert variant="success" className="h-full" border>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <CheckCircleOutlineIcon
                  fontSize="small"
                  className="text-green-500"
                />
                <p className="text-sm text-green-950 font-semibold">
                  {tActivityCards.budgetRequestSubmitted.proposedSubmittedBy.replace(
                    '{org}',
                    proposingOrg?.name || tActivityCards.notAvailable,
                  )}
                </p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">
                  {tActivityCards.budgetProposalAccepted.acceptedAmountLabel}
                </span>{' '}
                {formattedProposed}
              </p>
              <p className="flex gap-1 text-sm text-neutral-800">
                <span className="font-semibold">{tActivityCards.timelineLabel}</span>
                {formatDate(proposedStart, tActivityCards.notAvailable)} -{' '}
                {formatDate(proposedEnd, tActivityCards.notAvailable)}
              </p>
            </div>
          </Alert>

          <Alert variant="warning" className="h-full" border>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <CreateNewFolderOutlinedIcon
                  fontSize="small"
                  className="text-yellow-950 -mt-1"
                />
                <p className="text-sm text-yellow-950 font-semibold">
                  {tActivityCards.createdByLabel.replace('{org}', originatorOrg?.name || tActivityCards.notAvailable)}
                </p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">{tActivityCards.maxBidLabel}</span>{' '}
                {formattedMaxBudget}
              </p>
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">{tActivityCards.timelineLabel}</span>{' '}
                {formatDate(requestedStart, tActivityCards.notAvailable)} -{' '}
                {formatDate(requestedEnd, tActivityCards.notAvailable)}
              </p>
              {recipients.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-neutral-800 font-semibold">
                    {tActivityCards.sentToLabel}
                  </p>
                  {recipients.map((r: any) => (
                    <Badge
                      key={r.recipientOrganizationId}
                      size="sm"
                      variant="primary">
                      {r.recipientOrganization?.name || tActivityCards.notAvailable}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Alert>

          {activity.budgetRequest?.description && (
            <Alert className="h-full">
              <div className="flex items-start justify-between">
                {activity.budgetRequest?.description}
              </div>
            </Alert>
          )}
        </div>
      </div>
    </Card>
  );
};
